import { MlsResoStandardStatus, ParcelContactClassification, ParcelOwnerStatus, Query, SellerListBuilderPreviewInput } from '__generated__/graphql'
import { Effect, Match, RequestResolver, pipe } from 'effect'
import { UnknownException } from 'effect/Cause'
import { ListCriteria } from 'features/ListBuilder/domain/ListCriteria'
import { PropertyList } from 'features/ListBuilder/domain/ListSource'
import { ListPreview } from 'features/ListBuilder/domain/ListSource/ListPreview'
import { PropertyListData, PropertyListDataId } from 'features/ListBuilder/domain/ListSource/PropertyList'
import { SELLER_LIST_BUILDER_PREVIEW_QUERY } from 'features/ListBuilder/infra/repo/ListSourceRepoLive/BuildPropertyListResolver/schema'
import ListSourceRequest from 'features/ListBuilder/infra/repo/ListSourceRepoLive/ListSourceRequest'
import CriteriaSchema from 'features/ListBuilder/infra/repo/schema/CriteriaSchema'
import { EquityType } from 'features/valueObjects/EquityType'
import ParcelId from 'features/valueObjects/ParcelId'
import { apolloClient } from 'presentation/libs/client'
import UnitConverter from 'utils/UnitConverter'
import { isNonNullable } from 'utils/isNonNullable'

const BuildPropertyListResolver = RequestResolver.fromEffect(
  (request: ListSourceRequest.BuildList<PropertyList>) => pipe(
    Effect.sync(() =>
      CriteriaSchema.toSellerListBuilderPreviewInput(request.criteria),
    ),
    Effect.flatMap(input =>
      Effect.tryPromise({
        try: async () =>
          await apolloClient.query<
            SellerListBuilderPreviewResponse,
            SellerListBuilderPreviewVariables
          >({
            query: SELLER_LIST_BUILDER_PREVIEW_QUERY as any,
            variables: { input },
            fetchPolicy: 'no-cache',
          }),
        catch: error => new UnknownException({ message: String(error) }),
      }),
    ),
    Effect.flatMap(result =>
      result.data?.sellerListBuilderPreview
        ? Effect.succeed(toDomain(request.criteria, result.data.sellerListBuilderPreview))
        : Effect.dieMessage('query SellerListBuilderPreview failed'),
    ),
    Effect.catchTag('UnknownException', v => Effect.die(v)),
  ),
)

export default BuildPropertyListResolver

type SellerListBuilderPreviewResponse = Pick<Query, 'sellerListBuilderPreview'>
type SellerListBuilderPreviewVariables = {
  input: SellerListBuilderPreviewInput
}

export const toDomain = (
  criteria: ListCriteria,
  previewItems: Query['sellerListBuilderPreview'],
): ListPreview<PropertyList> => ({
  criteria,
  items: previewItems.map((item): PropertyListData => {
    const firstListing = item.mlsPropertyListings?.edges[0]?.node
    const firstResoListing = firstListing?.resoPropertyListing

    const bathroomsFull = item.buildingDetails.bathroomsCount.full ?? null
    const bathroomsHalf = item.buildingDetails.bathroomsCount.half ?? null

    const addressLocation = item.address?.location
    const landLocation = item.landDetails.location

    const ownerAddress = item.ownerDetails.contactAddress
    const loanToValue = toFiniteNumberOrNull(item.valuationDetails.loanToValue)

    const data: PropertyListData = {
      // #region App Info
      id: PropertyListDataId.from(item.id),
      parcelId: ParcelId.fromString(item.id),
      // #endregion

      // #region Address Info
      address: {
        line1: item.address?.line1 ?? null,
        city: item.address?.city ?? null,
        state: item.address?.state ?? null,
        subdivision: item.landDetails.subdivision ?? null,
        postalCode: item.address?.postalCode ?? null,
      },
      location: isNonNullable(addressLocation?.latitude)
      && isNonNullable(addressLocation?.longitude)
        ? {
          latitude: addressLocation.latitude,
          longitude: addressLocation.longitude,
        }
        : isNonNullable(landLocation?.latitude)
        && isNonNullable(landLocation?.longitude)
          ? {
            latitude: landLocation.latitude,
            longitude: landLocation.longitude,
          }
          : null,
      // #endregion

      // #region Value Info
      lastSoldDate: item.lastSale?.saleDate ?? null,
      estimatedValue: item.valuationDetails.value ?? null,
      estimatedEquity: item.valuationDetails.equity ?? null,
      listPrice: toFiniteNumberOrNull(firstResoListing?.listPrice),
      salePrice: item.lastSale?.saleAmount ?? null,
      // #endregion

      // #region Property Info
      useType: item.landDetails.standardizedUseType ?? null,
      // #endregion

      // #region Stats
      bedroomsCount: item.buildingDetails.bedroomsCount ?? null,
      bathroomsCountInfo: {
        full: bathroomsFull,
        half: bathroomsHalf,
        total: item.buildingDetails.bathroomsCount.total
        ?? pipe(
          Match.value([bathroomsFull, bathroomsHalf]),
          Match.when([Match.number, undefined], ([full]) => `${full}`),
          Match.when([undefined, Match.number], ([_, half]) => `.${half}`),
          Match.when([Match.number, Match.number], ([full, half]) => `${full}.${half}`),
          Match.orElse(() => null),
        ),
      },
      garageSpacesCount: item.buildingDetails.garageSpacesCount ?? null,
      buildingAreaSqft: item.buildingDetails.buildingAreaSqft ?? null,
      livingAreaSqft: item.buildingDetails.livingAreaSqft ?? null,
      lotAreaSqft: item.landDetails.lotAreaSqft ?? null,
      lotAreaAcres: item.landDetails.lotAreaAcres
      ?? (item.landDetails.lotAreaSqft
        ? UnitConverter.sqftToAcre(item.landDetails.lotAreaSqft)
        : null),
      yearBuilt: toFiniteNumberOrNull(item.buildingDetails.yearBuilt),
      // #endregion

      // #region Owner/Value Info
      ownerName: item.ownerDetails.owners[0]?.name.full ?? null,
      ownerAddress: {
        line1: ownerAddress?.line1 ?? null,
        city: ownerAddress?.city ?? null,
        state: ownerAddress?.state ?? null,
        postalCode: ownerAddress?.postalCode ?? null,
      },
      photos: (firstListing?.media.edges ?? [])
        .map(edge => edge?.node?.mediaURL
          ? {
            url: edge.node.mediaURL,
            caption: edge.node.shortDescription ?? null,
          }
          : null)
        .filter(isNonNullable),
      // #endregion

      // #region Owner/Value Info
      classification: mapOwnerClassificationFromGql(
        item.ownerDetails.ownerClassification,
        item.ownerDetails.ownerStatus,
      ),
      equityType: loanToValue === null ? null : EquityType.fromLtv(loanToValue),
      isVacant: item.ownerDetails.isVacant ?? null,
      isForeclosure: item.ownerDetails.isForeclosure ?? null,
      isSenior: item.ownerDetails.isSenior ?? null,
      isHomestead: item.ownerDetails.isHomestead ?? null,
      isTaxDelinquent: item.ownerDetails.isTaxDelinquent ?? null,
      // #endregion

      // #region MLS Info
      mlsStatus: mapMlsStatusFromGql(firstResoListing?.standardStatus),
      // #endregion
    }

    return data
  }),
})

const mapMlsStatusFromGql = (
  status: MlsResoStandardStatus | null | undefined,
): PropertyListData['mlsStatus'] => {
  switch (status) {
  case MlsResoStandardStatus.Active:
  case MlsResoStandardStatus.ComingSoon:
    return 'FOR_SALE'
  case MlsResoStandardStatus.ActiveUnderContract:
  case MlsResoStandardStatus.Pending:
    return 'PENDING'
  case MlsResoStandardStatus.Closed:
    return 'SOLD'
  case MlsResoStandardStatus.Expired:
    return 'EXPIRED'
  case MlsResoStandardStatus.Canceled:
    return 'CANCELED'
  case MlsResoStandardStatus.Withdrawn:
    return 'WITHDRAWN'
  default:
    return 'OFF_MARKET'
  }
}

const mapOwnerClassificationFromGql = (
  ownerClassification: ParcelContactClassification | null | undefined,
  ownerStatus: ParcelOwnerStatus | null | undefined,
): PropertyListData['classification'] => {
  if (ownerClassification === ParcelContactClassification.Trust)
    return 'TRUST'
  if (ownerClassification === ParcelContactClassification.Bank)
    return 'BANK'
  if (ownerClassification === ParcelContactClassification.Corporate)
    return 'CORPORATE'
  if (ownerStatus === ParcelOwnerStatus.OwnerOccupied)
    return 'OWNER_OCCUPIED'
  if (ownerStatus === ParcelOwnerStatus.InState)
    return 'IN_STATE'
  if (ownerStatus === ParcelOwnerStatus.OutOfState)
    return 'OUT_OF_STATE'
  if (ownerStatus === ParcelOwnerStatus.OutOfCountry)
    return 'OUT_OF_COUNTRY'
  if (ownerStatus === ParcelOwnerStatus.Military)
    return 'MILITARY'
  return null
}

const toFiniteNumberOrNull = (
  value: number | string | null | undefined,
): number | null => {
  if (value == null) return null

  const parsed = Number(value)

  return Number.isFinite(parsed) ? parsed : null
}
